import { AbstractComponentFlow } from './AbstractComponentFlow';
import { IProcessManifestProps } from '../lifecycle';
import { ManifestStore, ManifestComponentExtensionType } from '../stores';
import { OOIManifestBuilder } from '@wix/editor-platform-builder-manifest-adapter';
import { ComponentType } from '@wix/ambassador-devcenter-v1-component/types';
import {
  OOIWidgetUnresolvedStatics,
  ResolvedOOIWidgetStatics,
  InternalResolvedOOIWidgetStatics,
  ResolveManifestOverridesFn,
  SettingsAction,
  DefaultLayout,
  EditorElementLayout,
  ResizeDirection,
} from '@wix/editor-platform-ooi-metadata-resolver';

export class OOIComponentFlow extends AbstractComponentFlow<ManifestStore> {
  static extensionType = ManifestComponentExtensionType.OOI as const;
  public readonly type = OOIComponentFlow.extensionType;

  // we don't have panels to load for OOI components
  async loadPanels() {}

  async #importUMD(
    url: string,
    module = { exports: {} },
  ): Promise<{
    statics: ResolvedOOIWidgetStatics;
    bundle: OOIWidgetUnresolvedStatics;
  }> {
    // eslint-disable-next-line no-restricted-syntax, no-new-func
    const bundle = (new Function(
      'module',
      'exports',
      await (await fetch(url)).text(),
    ).call(module, module, module.exports),
    module).exports as OOIWidgetUnresolvedStatics;

    const statics = await bundle.createBuilderConfig({
      siteLanguage: this.dm.generalInfo.getRegionalLanguage() || 'en',
      userLanguage: this.dm.generalInfo.getLanguage() || 'en',
    });

    return { statics, bundle };
  }

  async loadManifest({
    compTypes,
    appDataComponent,
    isPendingApplication,
  }: IProcessManifestProps) {
    if (!appDataComponent.data || !appDataComponent.componentId) {
      return;
    }

    const builderConfigUrl =
      (appDataComponent.data as any)?.builderConfigUrl || '';
    let statics: ResolvedOOIWidgetStatics | null = null;
    let resolveManifestOverrides: ResolveManifestOverridesFn | undefined;
    let nativeSettingsPanelUrl: string | null = null;
    let hasLegacySettingsPanel: boolean = false;

    if (builderConfigUrl) {
      // Tier 1: Use existing UMD import
      try {
        const result = await this.#importUMD(builderConfigUrl);
        statics = result.statics;
        resolveManifestOverrides = statics.resolveManifestOverrides;
        const { url, isLegacySettingsPanel } = this.#getNativeSettingsPanelUrl(
          appDataComponent,
          statics.settingsAction,
        );
        nativeSettingsPanelUrl = url;
        hasLegacySettingsPanel = isLegacySettingsPanel || false;
      } catch (error) {
        console.warn(
          `Failed to import UMD for ${appDataComponent.componentId}:`,
          error,
        );
      }
    } else {
      // Tier 2: Derive from client spec map
      statics = this.#deriveStaticsFromClientSpecMap(appDataComponent);
      const { url, isLegacySettingsPanel } =
        this.#getNativeSettingsPanelUrl(appDataComponent);
      const compType = appDataComponent?.data?.refType;
      if (statics && compType && this.#isTpaPage(compType)) {
        statics.resizeDirection = ResizeDirection.horizontal;
      }
      nativeSettingsPanelUrl = url;
      hasLegacySettingsPanel = isLegacySettingsPanel || false;
    }

    if (!statics) {
      // Neither approach worked - log and return
      console.warn(
        `No statics could be derived for ${appDataComponent.componentId}`,
      );

      return;
    }

    const description =
      this.#getDescriptionsFromClientSpecMap(appDataComponent);

    const staticsWithDescription: InternalResolvedOOIWidgetStatics = {
      ...statics,
      description,
    };

    const layout = this.#getDefaultLayout(appDataComponent, statics);

    const manifest = new OOIManifestBuilder({
      statics: staticsWithDescription,
      nativeSettingsPanelUrl,
      layout,
    }).build({ isMobile: false });

    compTypes.forEach((compType) => {
      this.manifestStore.setManifest(
        compType,
        ManifestComponentExtensionType.OOI,
        manifest,
        {
          statics: staticsWithDescription,
          ...(nativeSettingsPanelUrl ? { hasLegacySettingsPanel } : {}),
          resolveManifestOverrides,
        },
        isPendingApplication,
      );
    });
  }

  #getNativeSettingsPanelUrl(
    appDataComponent: IProcessManifestProps['appDataComponent'],
    settingsAction?: SettingsAction,
  ):
    | { url: string; isLegacySettingsPanel: boolean }
    | { url: null; isLegacySettingsPanel: undefined } {
    if (settingsAction?.hidden || !appDataComponent.data) {
      return { url: null, isLegacySettingsPanel: undefined };
    }

    const settingsPanelUrlFromCsm =
      appDataComponent.data?.widgetData?.settingsEndpointUrl ||
      appDataComponent.data?.pageData?.settingsEndpointUrl ||
      appDataComponent.data?.settingsEndpointUrl;

    if (settingsAction?.url) {
      return {
        url: settingsAction.url,
        isLegacySettingsPanel: false,
      };
    }

    if (settingsPanelUrlFromCsm) {
      return {
        url: settingsPanelUrlFromCsm,
        isLegacySettingsPanel: true,
      };
    }
    return { url: null, isLegacySettingsPanel: undefined };
  }

  #deriveStaticsFromClientSpecMap(
    appDataComponent: IProcessManifestProps['appDataComponent'],
  ): ResolvedOOIWidgetStatics | null {
    const appDefId = this.clientSpecMapService.getAppDefId(
      appDataComponent?.componentId,
    );
    const appData = this.dm.platform.getAppDataByAppDefId(appDefId ?? '');
    const dashboardComponent = appData?.components?.find(
      (component) => component.type === ComponentType.DASHBOARD,
    );
    const dashboardPageId = dashboardComponent?.componentId;

    return {
      displayName: appDataComponent?.name ?? '',

      ...(dashboardPageId && { dashboardAction: { dashboardPageId } }),
      styleParams: {}, // Empty for tier 2
      settingsParams: {}, // Empty for tier 2
    };
  }

  #getDefaultLayout(
    appDataComponent: IProcessManifestProps['appDataComponent'],
    statics: ResolvedOOIWidgetStatics,
  ): EditorElementLayout {
    const compType = appDataComponent?.data?.refType;

    switch (compType) {
      case 'wysiwyg.viewer.components.tpapps.TPAGluedWidget':
        return {
          ...DefaultLayout,
          // controls `pinnable` behavior
          disablePositioning: true,
        };
      default: {
        const { resizeDirection } = statics;
        return {
          ...DefaultLayout,
          ...(resizeDirection && { resizeDirection }),
        };
      }
    }
  }

  #isTpaPage(compType: string): boolean {
    return (
      compType === 'wysiwyg.viewer.components.tpapps.TPAMultiSection' ||
      compType === 'wysiwyg.viewer.components.tpapps.TPASection'
    );
  }

  #getDescriptionsFromClientSpecMap(
    appDataComponent: IProcessManifestProps['appDataComponent'],
  ): string | undefined {
    const componentData = this.clientSpecMapService.getAppComponentData(
      appDataComponent.data.componentType,
    );
    if (!componentData) {
      return undefined;
    }

    if (componentData.type === ComponentType.WIDGET_OUT_OF_IFRAME) {
      // @ts-expect-error wrong DM types
      return componentData.data?.widgetData?.widgetDisplay?.shortDescription;
    }

    if (componentData.type === ComponentType.PAGE_OUT_OF_IFRAME) {
      // @ts-expect-error wrong DM types
      return componentData.data?.pageData?.description;
    }

    return undefined;
  }
}
